import client from './RestClient';
import fileDownload from 'js-file-download';
import { ReportDimension, ReportGran, ReportData, ReportType, ECConvDays } from 'core/report/ReportData';
import _ from 'lodash';
import { AxiosInstance } from 'axios';
import moment from 'moment';

export const defaultFormat = 'YYYY-MM-DD';

export enum ReconciliationReportType {
  MONTH = 'month',
  STANDARD = 'standard'
}

export interface ReportWebService {
  downloadL1ObjectReport (order: {
    name: string,
    l1ObjectId: number
  }, startDate: string, endDate: string, groupBy: string, selectFields: string[]): Promise<void>;
  getReportData (type: ReportType, dimension: ReportDimension, gran: ReportGran, filter: any, from: string, to: string, ecConvDays?: ECConvDays): Promise<ReportData>;
  downloadTenmaxReconciliationReport (reportType: ReconciliationReportType, from: string, to: string): Promise<void>;
  downloadStoredValueReconciliationReport (from: string, to: string): Promise<void>;
}

export class RestfulReportWebService implements ReportWebService {
  restClient: AxiosInstance;
  defualtFileDownload: any;

  constructor (restClient: AxiosInstance = client, defualtFileDownload: any = fileDownload) {
    this.restClient = restClient;
    this.defualtFileDownload = defualtFileDownload;
  }

  async downloadL1ObjectReport (l1Object: {
    name: string,
    l1ObjectId: number
  }, startDate: string, endDate: string, groupBy: string, selectFields: string[]): Promise<void> {
    const url = `/v2/report/export/l1-object?l1ObjectId=${l1Object.l1ObjectId}&groupBy=${groupBy}&metrics=${selectFields.join(',')}&from=${startDate}&to=${endDate}`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, `${l1Object.name}.xlsx`);
  }

  async getReportData (type: ReportType, dimension: ReportDimension, gran: ReportGran, filter: any, from: string, to: string, ecConvDays?: ECConvDays) {
    const filterQueryParams = Object.keys(filter).map(key => `${key}=${filter[key]}`).join('&');
    const reportType = type === ReportType.PERFORMANCE ? 'channel-performance' : type.toLowerCase();
    let url = `report/${reportType}?dimension=${dimension}&gran=${gran}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&${filterQueryParams}`;
    ecConvDays && (url += `&conv-days=${ecConvDays}`);
    const response = await this.restClient.get(url);
    return wrapReportData(response.data);
  }

  async downloadTenmaxReconciliationReport (reportType: ReconciliationReportType, from: string, to: string): Promise<void> {
    let url: string;
    let fileName: string;
    const dateInfo = `${moment(from).format(defaultFormat)}_${moment(to).format(defaultFormat)}`;
    if (reportType === ReconciliationReportType.MONTH) {
      url = `/v2/report/export/reconciliation?from=${from}&to=${to}`;
      fileName = `ReconciliationReport_${dateInfo}.xlsx`;
    } else {
      url = `/v2/report/export/tenmax-reconciliation?from=${from}&to=${to}`;
      fileName = `TenmaxReconciliationReport_${dateInfo}.csv`;
    }
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, fileName);
  }

  async downloadStoredValueReconciliationReport (from: string, to: string): Promise<void> {
    const url = `/v2/report/export/stored-value-reconciliation?from=${from}&to=${to}`;
    const dateInfo = `${moment(from).format(defaultFormat)}_${moment(to).format(defaultFormat)}`;
    const fileName = `StoredValueReconciliation_${dateInfo}.xlsx`;
    const response = await this.restClient.get(url, { responseType: 'blob' });
    this.defualtFileDownload(response.data, fileName);
  }
}

const wrapReportData = (json: any) => {
  return {
    allowMetrics: _.get(json, 'allowMetrics', []),
    dimension: _.get(json, 'dimension'),
    filter: _.get(json, 'filter', {}),
    from: _.get(json, 'from'),
    to: _.get(json, 'to'),
    records: _.get(json, 'records', []).map(record => wrapRecord(record)),
    currency: _.get(json, 'currency')
  };
};

const wrapRecord = (json: any) => {
  return {
    ...json,
    tags: _.compact(_.get(json, 'tags', '').split(','))
  };
};
